/**
 * PagedOutput Component
 * Output display with pagination support for multiple results
 */

import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { copyImageUrlToClipboard } from '../../utils/clipboardUtils';
import { createPortal } from 'react-dom';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { MarkdownTextarea, PromptImprovementContext } from './MarkdownTextarea';
import { Lightbox } from './Lightbox';
import { ImageActions } from './ImageActions';
import { VideoActions } from './VideoActions';
import { VideoTrimModal } from './VideoTrimModal';
import { ImageUploadZone, ImageData } from './ImageUploadZone';
import { normalizeMediaUrl, getAuthenticatedMediaUrlSync } from '../../utils/mediaUrl';
import { generateMediaFilename } from '../../utils/markdownToRtf';
import { useAuth } from '../../contexts/AuthContext';
import './PagedOutput.css';

export interface PagedOutputProps {
  content: string;
  pages?: Array<{
    content: string;
    context?: any;
    type: 'text' | 'image' | 'video' | 'audio';
    timestamp: string;
    aspectRatio?: number; // Cached aspect ratio for image/video
  }>;
  currentPageIndex?: number;
  onPageChange?: (index: number) => void;
  onChange?: (value: string) => void;
  onLightboxReady?: (openFn: () => void) => void;
  onCondenseRequest?: (text: string) => Promise<string>;  // For modal: returns condensed text
  onVideoTrim?: (trimmedVideoUrl: string) => void;
  onMediaMetadataReady?: (pageIndex: number, aspectRatio: number, width: number, height: number) => void; // Callback when media dimensions are detected
  isCondensing?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
  minHeight?: string;
  maxHeight?: string;
  streamingInProgress?: boolean;
  showLightboxButton?: boolean;
  // Context menu callbacks for fullscreen
  onSendToDevice?: (url: string, type: string) => void;
  onSetFlowThumbnail?: (url: string) => void;
  onDeleteOutput?: (pageIndex: number) => void;
  onDuplicateOutput?: (url: string, type: string) => void; // Create duplicate Image Container or Output node
  onPlaceFrameOnStage?: (dataUrl: string, blob: Blob) => void; // Place a video frame as a new image node on the canvas
  disableContextMenu?: boolean;
  promptImprovementContext?: PromptImprovementContext; // Context for AI prompt improvement
  nodeTitle?: string; // Title for the text editor modal
  outputLabel?: string; // Label used for downloaded filenames (apiOutputLabel)
  placeholder?: string; // Placeholder text when content is empty
  /** 'placeholder' = textarea (default), 'blank' = nothing, 'dropzone' = upload zone */
  emptyBehavior?: 'placeholder' | 'blank' | 'dropzone';
  onUploadMedia?: (imageData: ImageData) => void;
}

// mm:ss for the audio duration badge — the audio counterpart of the WxH readout
// the image/video arms show in the same badge slot.
const formatAudioDuration = (seconds: number): string => {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export const PagedOutput: React.FC<PagedOutputProps> = ({
  content,
  pages = [],
  currentPageIndex = 0,
  onPageChange,
  onChange,
  onLightboxReady,
  onCondenseRequest,
  onVideoTrim,
  onMediaMetadataReady,
  isCondensing = false,
  variant = 'neutral',
  streamingInProgress = false,
  showLightboxButton: _showLightboxButton = true,
  onSendToDevice,
  onSetFlowThumbnail,
  onDeleteOutput,
  onDuplicateOutput,
  onPlaceFrameOnStage,
  disableContextMenu = false,
  promptImprovementContext,
  nodeTitle,
  outputLabel,
  minHeight,
  maxHeight,
  placeholder = 'Output will appear here...',
  emptyBehavior = 'placeholder',
  onUploadMedia,
}) => {
  const { user } = useAuth();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPageIndex, setLightboxPageIndex] = useState(0);
  const [videoTrimModalOpen, setVideoTrimModalOpen] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [mediaDimensions, setMediaDimensions] = useState<{ w: number; h: number } | null>(null);
  // Audio has no pixel dimensions — duration fills the same badge slot instead.
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const MAX_RETRIES = 3;
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; pageIndex: number } | null>(null);
  const [frameSubmenu, setFrameSubmenu] = useState<'current' | 'last' | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  
  // Check if current page already has aspect ratio cached
  const currentPage = pages.length > 0 && pages[currentPageIndex]
    ? pages[currentPageIndex]
    : null;
  const hasAspectRatio = currentPage?.aspectRatio && currentPage.aspectRatio > 0;
  
  // Reset media error state when page or content changes
  useEffect(() => {
    setMediaError(false);
    setRetryCount(0);
    setMediaDimensions(null);
    setAudioDuration(null);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, [currentPageIndex, content, currentPage?.content]);
  
  // Close context menu when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const closeMenu = () => {
      setContextMenu(null);
      setFrameSubmenu(null);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', closeMenu, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', closeMenu, true);
      };
    }
    return undefined;
  }, [contextMenu]);
  
  // Handle context menu for images/videos
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (disableContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, pageIndex: currentPageIndex });
  }, [currentPageIndex, disableContextMenu]);
  
  // Handle download
  const handleDownload = useCallback((pageIndex: number) => {
    const url = pages.length > 0 ? pages[pageIndex]?.content : content || '';
    const type = (pages.length > 0 ? pages[pageIndex]?.type : 'image') as 'image' | 'video' | 'audio';
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = generateMediaFilename(type, { nodeTitle: outputLabel || nodeTitle, pageIndex: pages.length > 1 ? pageIndex : undefined, url });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pages, content, nodeTitle, outputLabel]);

  // Capture the current video frame as a PNG blob
  const captureCurrentFrame = useCallback((): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return Promise.resolve(null);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.resolve(null);
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }, []);

  // Capture the last frame of the video as a PNG blob
  const captureLastFrame = useCallback((): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || !video.src) return Promise.resolve(null);
    return new Promise((resolve) => {
      // Clone the video to seek to the last frame without affecting playback
      const tempVideo = document.createElement('video');
      tempVideo.crossOrigin = 'anonymous';
      tempVideo.muted = true;
      tempVideo.preload = 'auto';
      tempVideo.src = video.src;
      tempVideo.oncanplay = () => {
        // Seek to just before the end to get the last frame
        tempVideo.currentTime = Math.max(0, tempVideo.duration - 0.05);
      };
      tempVideo.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = tempVideo.videoWidth;
        canvas.height = tempVideo.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(tempVideo, 0, 0);
        canvas.toBlob((blob) => {
          tempVideo.src = '';
          resolve(blob);
        }, 'image/png');
      };
      tempVideo.onerror = () => resolve(null);
      tempVideo.load();
    });
  }, []);

  // Copy a frame blob to clipboard
  const handleCopyFrameToClipboard = useCallback((capturer: () => Promise<Blob | null>) => {
    capturer().then((blob) => {
      if (blob) {
        navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]).catch(err => console.error('Failed to copy frame:', err));
      }
    });
  }, []);

  // Place a frame blob on stage as a new image node
  const handlePlaceFrameOnStage = useCallback((capturer: () => Promise<Blob | null>) => {
    if (!onPlaceFrameOnStage) return;
    capturer().then((blob) => {
      if (!blob) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Pass both the data URL (for instant preview) and the blob (so the
          // parent can upload to R2 and register the asset).
          onPlaceFrameOnStage(reader.result, blob);
        }
      };
      reader.readAsDataURL(blob);
    });
  }, [onPlaceFrameOnStage]);

  // Check for already-loaded media on mount or when content changes (handles cached images/videos)
  useEffect(() => {
    // Check image
    if (imageRef.current) {
      const img = imageRef.current;
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setMediaDimensions({ w: img.naturalWidth, h: img.naturalHeight });
        if (!hasAspectRatio && onMediaMetadataReady) {
          const aspectRatio = img.naturalWidth / img.naturalHeight;
          onMediaMetadataReady(currentPageIndex, aspectRatio, img.naturalWidth, img.naturalHeight);
        }
      }
    }

    // Check video
    if (videoRef.current) {
      const video = videoRef.current;
      if (video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0) {
        setMediaDimensions({ w: video.videoWidth, h: video.videoHeight });
        if (!hasAspectRatio && onMediaMetadataReady) {
          const aspectRatio = video.videoWidth / video.videoHeight;
          onMediaMetadataReady(currentPageIndex, aspectRatio, video.videoWidth, video.videoHeight);
        }
      }
    }
  }, [currentPageIndex, hasAspectRatio, onMediaMetadataReady]);
  
  // Provide the lightbox open function to parent
  React.useEffect(() => {
    if (onLightboxReady) {
      onLightboxReady(() => {
        // Pause any playing node video before opening lightbox
        if (videoRef.current) {
          videoRef.current.pause();
        }
        setLightboxPageIndex(currentPageIndex);
        setLightboxOpen(true);
      });
    }
  }, [onLightboxReady, currentPageIndex]);

  const handlePreviousPage = useCallback(() => {
    if (currentPageIndex > 0 && onPageChange) {
      onPageChange(currentPageIndex - 1);
    }
  }, [currentPageIndex, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPageIndex < pages.length - 1 && onPageChange) {
      onPageChange(currentPageIndex + 1);
    }
  }, [currentPageIndex, pages.length, onPageChange]);

  const handleImageClick = useCallback(() => {
    setLightboxPageIndex(currentPageIndex);
    setLightboxOpen(true);
  }, [currentPageIndex]);

  const handleVideoDoubleClick = useCallback((e: React.MouseEvent<HTMLVideoElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Pause the node video before opening lightbox
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setLightboxPageIndex(currentPageIndex);
    setLightboxOpen(true);
  }, [currentPageIndex]);

  const handleLightboxPageChange = useCallback((index: number) => {
    setLightboxPageIndex(index);
    onPageChange?.(index);
  }, [onPageChange]);

  // Handle image load to detect natural dimensions
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (width > 0 && height > 0) {
      setMediaDimensions({ w: width, h: height });
      if (onMediaMetadataReady) {
        const aspectRatio = width / height;
        onMediaMetadataReady(currentPageIndex, aspectRatio, width, height);
      }
    }
  }, [currentPageIndex, onMediaMetadataReady]);

  // Handle video load to detect natural dimensions
  const handleVideoLoadedMetadata = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width > 0 && height > 0) {
      setMediaDimensions({ w: width, h: height });
      if (onMediaMetadataReady) {
        const aspectRatio = width / height;
        onMediaMetadataReady(currentPageIndex, aspectRatio, width, height);
      }
    }
  }, [currentPageIndex, onMediaMetadataReady]);

  // Get content from current page (currentPage already defined above for aspect ratio check)
  const rawContent = currentPage ? currentPage.content : content;
  const contentType = currentPage ? currentPage.type : 'text';
  
  // Normalize URLs for media content and add authentication
  const displayContent = useMemo(() => {
    if (contentType === 'image' || contentType === 'video' || contentType === 'audio') {
      const normalized = normalizeMediaUrl(rawContent);
      // Add userId to proxy URLs for authentication
      return getAuthenticatedMediaUrlSync(normalized, user?.id || null);
    }
    return rawContent;
  }, [rawContent, contentType, user?.id]);

  const containerClasses = [
    'paged-output-container',
    variant
  ].filter(Boolean).join(' ');

  // Render content based on type
  const renderContent = () => {
    if (!displayContent) {
      if (emptyBehavior === 'dropzone' && onUploadMedia) {
        return (
          <ImageUploadZone
            onImageUpload={onUploadMedia}
          />
        );
      }
      if (emptyBehavior === 'blank') {
        return <div style={{ flex: 1 }} />;
      }
      // default: 'placeholder'
      return (
        <MarkdownTextarea
          value=""
          onChange={onChange || (() => {})}
          placeholder={placeholder}
          variant={variant}
          minHeight={minHeight ?? "100%"}
          maxHeight={maxHeight ?? "100%"}
          showSubmitButton={false}
          onCondenseRequest={onCondenseRequest}
          promptImprovementContext={promptImprovementContext}
          nodeTitle={nodeTitle}
        />
      );
    }

    switch (contentType) {
      case 'image':
        // Show error placeholder if media failed to load
        if (mediaError) {
          return (
            <div 
              className="media-error-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '20px',
                backgroundColor: 'var(--color-bg-control)',
                borderRadius: '12px',
                gap: '8px'
              }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="var(--color-accent-error)"/>
              </svg>
              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
                Media not found
              </span>
              {onDeleteOutput && (
                <button
                  onClick={() => onDeleteOutput(currentPageIndex)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)'
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          );
        }
        return (
          <div 
            className="image-container"
            onContextMenu={handleContextMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '12px'
            }}>
            <img
              ref={imageRef}
              src={retryCount > 0 ? `${displayContent}${displayContent.includes('?') ? '&' : '?'}_r=${retryCount}` : displayContent}
              alt="Output"
              onClick={handleImageClick}
              onMouseDown={(e) => {
                e.currentTarget.style.cursor = 'grabbing';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.cursor = 'pointer';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.cursor = 'pointer';
                e.currentTarget.style.opacity = '1';
              }}
              onLoad={(e) => { handleImageLoad(e); setMediaError(false); setRetryCount(0); }}
              onError={() => {
                if (retryCount < MAX_RETRIES) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
                  retryTimerRef.current = setTimeout(() => setRetryCount(c => c + 1), delay);
                } else {
                  setMediaError(true);
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
              title="Click to expand"
            />
            {mediaDimensions && (
              <div className="media-resolution-badge">
                {mediaDimensions.w}×{mediaDimensions.h}
              </div>
            )}
            <ImageActions
              show={true}
              onSendToDevice={onSendToDevice ? () => onSendToDevice(displayContent, 'image') : undefined}
              onDownload={() => handleDownload(currentPageIndex)}
              onDuplicate={onDuplicateOutput ? () => onDuplicateOutput(displayContent, 'image') : undefined}
              onCopyToClipboard={() => {
                copyImageUrlToClipboard(displayContent)
                  .catch(err => console.error('Failed to copy image:', err));
              }}
              onDelete={onDeleteOutput ? () => onDeleteOutput(currentPageIndex) : undefined}
              showDelete={!!onDeleteOutput}
              showSendToDevice={!!onSendToDevice}
              showDownload={true}
              showDuplicate={!!onDuplicateOutput}
              showCopyToClipboard={true}
            />
          </div>
        );

      case 'video':
        if (mediaError) {
          return (
            <div 
              className="media-error-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '20px',
                backgroundColor: 'var(--color-bg-control)',
                borderRadius: '12px',
                gap: '8px'
              }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <polygon points="10 8 16 12 10 16 10 8"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="var(--color-accent-error)"/>
              </svg>
              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
                Video not found
              </span>
              {onDeleteOutput && (
                <button
                  onClick={() => onDeleteOutput(currentPageIndex)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)'
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          );
        }
        return (
          <div 
            className="video-container"
            onContextMenu={handleContextMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '12px'
            }}
          >
            <video
              ref={videoRef}
              src={retryCount > 0 ? `${displayContent}${displayContent.includes('?') ? '&' : '?'}_r=${retryCount}` : displayContent}
              controls
              loop
              onLoadedMetadata={(e) => { handleVideoLoadedMetadata(e); setMediaError(false); setRetryCount(0); }}
              onError={() => {
                if (retryCount < MAX_RETRIES) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
                  retryTimerRef.current = setTimeout(() => setRetryCount(c => c + 1), delay);
                } else {
                  setMediaError(true);
                }
              }}
              onDoubleClick={handleVideoDoubleClick}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              title="Double-click to expand"
            />
            {mediaDimensions && (
              <div className="media-resolution-badge">
                {mediaDimensions.w}×{mediaDimensions.h}
              </div>
            )}
            <VideoActions
              show={true}
              onTrim={() => setVideoTrimModalOpen(true)}
              onSendToDevice={onSendToDevice ? () => onSendToDevice(displayContent, 'video') : undefined}
              onDownload={() => handleDownload(currentPageIndex)}
              onDuplicate={onDuplicateOutput ? () => onDuplicateOutput(displayContent, 'video') : undefined}
              onDelete={onDeleteOutput ? () => onDeleteOutput(currentPageIndex) : undefined}
              showDelete={!!onDeleteOutput}
              showTrim={false}  // Temporarily hidden - trim feature disabled
              showSendToDevice={!!onSendToDevice}
              showDownload={true}
              showDuplicate={!!onDuplicateOutput}
            />
            <VideoTrimModal
              open={videoTrimModalOpen}
              onClose={() => setVideoTrimModalOpen(false)}
              videoUrl={displayContent}
              onTrimComplete={(trimmedUrl) => {
                if (onVideoTrim) {
                  onVideoTrim(trimmedUrl);
                }
              }}
            />
          </div>
        );

      case 'audio':
        // Audio has no pixel dimensions and nothing to expand into a lightbox,
        // so the arm is deliberately simpler than image/video — but it keeps the
        // same retry ladder, the same error placeholder and the same action rail
        // so a Voice output behaves like any other generated media.
        if (mediaError) {
          return (
            <div
              className="media-error-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '20px',
                backgroundColor: 'var(--color-bg-control)',
                borderRadius: '12px',
                gap: '8px'
              }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="1.5">
                <path d="M3 10v4h4l5 5V5L7 10H3z"/>
                <line x1="4" y1="4" x2="20" y2="20" stroke="var(--color-accent-error)"/>
              </svg>
              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
                Audio not found
              </span>
              {onDeleteOutput && (
                <button
                  onClick={() => onDeleteOutput(currentPageIndex)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)'
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          );
        }
        return (
          <div
            className="audio-container"
            onContextMenu={handleContextMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              padding: '16px',
              backgroundColor: 'var(--color-bg-surface)',
              borderRadius: '12px'
            }}
          >
            <audio
              // The control bar is this element's ONLY interactive surface, so
              // without `nodrag` React Flow starts a node drag on every attempt
              // to hit play or scrub.
              className="nodrag"
              src={retryCount > 0 ? `${displayContent}${displayContent.includes('?') ? '&' : '?'}_r=${retryCount}` : displayContent}
              controls
              preload="metadata"
              onLoadedMetadata={(e) => {
                const el = e.currentTarget;
                if (Number.isFinite(el.duration) && el.duration > 0) setAudioDuration(el.duration);
                setMediaError(false);
                setRetryCount(0);
              }}
              onError={() => {
                if (retryCount < MAX_RETRIES) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
                  retryTimerRef.current = setTimeout(() => setRetryCount(c => c + 1), delay);
                } else {
                  setMediaError(true);
                }
              }}
              style={{ width: '100%' }}
            />
            {audioDuration !== null && (
              <div className="media-resolution-badge">
                {formatAudioDuration(audioDuration)}
              </div>
            )}
            <VideoActions
              show={true}
              onSendToDevice={onSendToDevice ? () => onSendToDevice(displayContent, 'audio') : undefined}
              onDownload={() => handleDownload(currentPageIndex)}
              onDuplicate={onDuplicateOutput ? () => onDuplicateOutput(displayContent, 'audio') : undefined}
              onDelete={onDeleteOutput ? () => onDeleteOutput(currentPageIndex) : undefined}
              showDelete={!!onDeleteOutput}
              showTrim={false}
              showSendToDevice={!!onSendToDevice}
              showDownload={true}
              showDuplicate={!!onDuplicateOutput}
            />
          </div>
        );

      case 'text':
      default:
        return (
          <MarkdownTextarea
            value={displayContent}
            onChange={streamingInProgress ? (() => {}) : (onChange || (() => {}))}
            placeholder={placeholder}
            variant={variant}
            minHeight={minHeight ?? "100%"}
            maxHeight={maxHeight ?? "100%"}
            showSubmitButton={false}
            readOnly={streamingInProgress}
            onCondenseRequest={onCondenseRequest}
            promptImprovementContext={promptImprovementContext}
            nodeTitle={nodeTitle}
          />
        );
    }
  };

  return (
    <div 
      className={containerClasses}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative'
      }}
    >
      {/* Content display */}
      <div
        className="paged-output-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'var(--color-bg-primary)',
          position: 'relative'
        }}
      >
        {renderContent()}
        
        {/* Pagination controls - show at top with gradient background, visible on parent hover */}
        {pages.length > 1 && (
          <div 
            className="paged-output-nav" 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '4px',
              padding: '12px 16px',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.3), transparent)',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: 'none',
              zIndex: 5
            }}
            onMouseEnter={() => {
              // Ensure pagination stays visible on direct hover
              const pagination = event?.currentTarget as HTMLElement;
              if (pagination) pagination.style.opacity = '1';
            }}
          >
            <button
              onClick={handlePreviousPage}
              disabled={currentPageIndex === 0}
              className="paged-output-nav-button"
              style={{
                padding: '0 4px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentPageIndex === 0 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '1',
                transition: 'all 0.2s ease',
                minWidth: '18px'
              }}
              title="Previous page"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              &lt;
            </button>
            
            <span style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: '500',
              pointerEvents: 'auto'
            }}>
              {currentPageIndex + 1}/{pages.length}
            </span>
            
            <button
              onClick={handleNextPage}
              disabled={currentPageIndex === pages.length - 1}
              className="paged-output-nav-button"
              style={{
                padding: '0 4px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                cursor: currentPageIndex === pages.length - 1 ? 'not-allowed' : 'pointer',
                opacity: currentPageIndex === pages.length - 1 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '1',
                transition: 'all 0.2s ease',
                minWidth: '18px'
              }}
              title="Next page"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              &gt;
            </button>
          </div>
        )}
      </div>

      {/* Lightbox for fullscreen viewing with navigation */}
      {lightboxOpen && (
        <Lightbox
          imageUrl={pages.length > 0 ? pages[lightboxPageIndex]?.content : content || ''}
          content={pages.length === 0 ? content : undefined}
          contentType={pages.length === 0 ? contentType : undefined}
          pages={pages}
          currentPageIndex={lightboxPageIndex}
          onPageChange={handleLightboxPageChange}
          onClose={() => setLightboxOpen(false)}
          onConfirm={!streamingInProgress && onChange ? onChange : undefined}
          showConfirmButtons={!streamingInProgress && !!onChange}
          onCondenseRequest={onCondenseRequest}
          isCondensing={isCondensing}
          onDownload={() => {
            const url = pages.length > 0 ? pages[lightboxPageIndex]?.content : content || '';
            const type = (pages.length > 0 ? pages[lightboxPageIndex]?.type : contentType) as 'image' | 'video';
            if (url) {
              const link = document.createElement('a');
              link.href = url;
              link.download = generateMediaFilename(type ?? 'image', { nodeTitle: outputLabel || nodeTitle, pageIndex: pages.length > 1 ? lightboxPageIndex : undefined, url });
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }}
          onSendToDevice={onSendToDevice ? () => {
            const url = pages.length > 0 ? pages[lightboxPageIndex]?.content : content || '';
            const type = pages.length > 0 ? pages[lightboxPageIndex]?.type : contentType;
            if (url) {
              onSendToDevice(url, type || 'image');
              setLightboxOpen(false);
            }
          } : undefined}
          onSetAsThumbnail={onSetFlowThumbnail && (pages.length > 0 ? pages[lightboxPageIndex]?.type === 'image' : contentType === 'image') ? () => {
            const url = pages.length > 0 ? pages[lightboxPageIndex]?.content : content || '';
            if (url) {
              onSetFlowThumbnail(url);
              setLightboxOpen(false);
            }
          } : undefined}
          showThumbnailButton={pages.length > 0 ? pages[lightboxPageIndex]?.type === 'image' : contentType === 'image'}
          onDelete={onDeleteOutput ? () => {
            onDeleteOutput(lightboxPageIndex);
            setLightboxOpen(false);
          } : undefined}
          onCopyToClipboard={(pages.length > 0 ? pages[lightboxPageIndex]?.type === 'image' : contentType === 'image') ? () => {
            const url = pages.length > 0 ? pages[lightboxPageIndex]?.content : content || '';
            if (url) {
              const normalized = normalizeMediaUrl(url);
              const authUrl = getAuthenticatedMediaUrlSync(normalized, user?.id || null);
              copyImageUrlToClipboard(authUrl)
                .catch(err => console.error('Failed to copy image:', err));
            }
          } : undefined}
          onDuplicate={onDuplicateOutput ? () => {
            const url = pages.length > 0 ? pages[lightboxPageIndex]?.content : content || '';
            const type = pages.length > 0 ? pages[lightboxPageIndex]?.type : contentType;
            if (url) {
              onDuplicateOutput(url, type || 'image');
              setLightboxOpen(false);
            }
          } : undefined}
        />
      )}

      {/* Context Menu Portal */}
      {contextMenu && createPortal(
        <div
          ref={contextMenuRef}
          className="paged-output-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000,
          }}
        >
          {onSendToDevice && (
            <button
              className="context-menu-item"
              onClick={() => {
                const page = pages[contextMenu.pageIndex];
                if (page) {
                  onSendToDevice(page.content, page.type);
                }
                setContextMenu(null);
              }}
            >
              <PhoneIphoneIcon fontSize="small" />
              <span>Send to Device</span>
            </button>
          )}
          {pages[contextMenu.pageIndex]?.type === 'image' && (
            <button
              className="context-menu-item"
              onClick={() => {
                const page = pages[contextMenu.pageIndex];
                if (page) {
                  const url = normalizeMediaUrl(page.content);
                  const authUrl = getAuthenticatedMediaUrlSync(url, user?.id || null);
                  copyImageUrlToClipboard(authUrl)
                    .catch(err => console.error('Failed to copy image:', err));
                }
                setContextMenu(null);
              }}
            >
              <ContentCopyIcon fontSize="small" />
              <span>Copy to Clipboard</span>
            </button>
          )}
          {pages[contextMenu.pageIndex]?.type === 'video' && (
            <>
              <div
                className={`context-menu-item has-submenu ${frameSubmenu === 'current' ? 'is-active' : ''}`}
                onMouseEnter={() => setFrameSubmenu('current')}
              >
                <PhotoCameraIcon fontSize="small" />
                <span>Current Frame</span>
                <span className="context-menu-chevron">›</span>
                {frameSubmenu === 'current' && (
                  <div
                    className="context-menu-submenu"
                    onMouseEnter={() => setFrameSubmenu('current')}
                    onMouseLeave={() => setFrameSubmenu(null)}
                  >
                    <button
                      className="context-menu-item"
                      onClick={() => {
                        handleCopyFrameToClipboard(captureCurrentFrame);
                        setContextMenu(null);
                        setFrameSubmenu(null);
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                      <span>Copy to Clipboard</span>
                    </button>
                    {onPlaceFrameOnStage && (
                      <button
                        className="context-menu-item"
                        onClick={() => {
                          handlePlaceFrameOnStage(captureCurrentFrame);
                          setContextMenu(null);
                          setFrameSubmenu(null);
                        }}
                      >
                        <AddToPhotosIcon fontSize="small" />
                        <span>Place on Stage</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div
                className={`context-menu-item has-submenu ${frameSubmenu === 'last' ? 'is-active' : ''}`}
                onMouseEnter={() => setFrameSubmenu('last')}
              >
                <SkipNextIcon fontSize="small" />
                <span>Last Frame</span>
                <span className="context-menu-chevron">›</span>
                {frameSubmenu === 'last' && (
                  <div
                    className="context-menu-submenu"
                    onMouseEnter={() => setFrameSubmenu('last')}
                    onMouseLeave={() => setFrameSubmenu(null)}
                  >
                    <button
                      className="context-menu-item"
                      onClick={() => {
                        handleCopyFrameToClipboard(captureLastFrame);
                        setContextMenu(null);
                        setFrameSubmenu(null);
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                      <span>Copy to Clipboard</span>
                    </button>
                    {onPlaceFrameOnStage && (
                      <button
                        className="context-menu-item"
                        onClick={() => {
                          handlePlaceFrameOnStage(captureLastFrame);
                          setContextMenu(null);
                          setFrameSubmenu(null);
                        }}
                      >
                        <AddToPhotosIcon fontSize="small" />
                        <span>Place on Stage</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          <button
            className="context-menu-item"
            onClick={() => {
              handleDownload(contextMenu.pageIndex);
              setContextMenu(null);
            }}
          >
            <DownloadIcon fontSize="small" />
            <span>Download</span>
          </button>
          {onDuplicateOutput && (
            <button
              className="context-menu-item"
              onClick={() => {
                const page = pages[contextMenu.pageIndex];
                if (page) {
                  onDuplicateOutput(page.content, page.type);
                }
                setContextMenu(null);
              }}
            >
              <AddToPhotosIcon fontSize="small" />
              <span>Duplicate</span>
            </button>
          )}
          {onSetFlowThumbnail && pages[contextMenu.pageIndex]?.type === 'image' && (
            <button
              className="context-menu-item"
              onClick={() => {
                const page = pages[contextMenu.pageIndex];
                if (page) {
                  onSetFlowThumbnail(page.content);
                }
                setContextMenu(null);
              }}
            >
              <ImageIcon fontSize="small" />
              <span>Set Knod Thumbnail</span>
            </button>
          )}
          {onDeleteOutput && (
            <>
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item--danger"
                onClick={() => {
                  onDeleteOutput(contextMenu.pageIndex);
                  setContextMenu(null);
                }}
              >
                <DeleteIcon fontSize="small" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PagedOutput;
